import type { HeaderContext } from "@tanstack/react-table";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useHover } from "../../../hooks";
import { cn } from "../../../utils";

export type SorterProps = {
  show?: boolean;
};

export type HeaderCellProps<TData = any, TValue = any> = {
  label: React.ReactNode;
  context: HeaderContext<TData, TValue>;
  rootClassName?: string;
  labelClassName?: string;
  sorterProps?: SorterProps;
  align?: "left" | "center" | "right";
};

const HeaderCell = ({
  rootClassName,
  labelClassName,
  context,
  label,
  sorterProps,
  align = "left"
}: HeaderCellProps) => {
  const { ref, hovering } = useHover();

  const showSorter = sorterProps?.show ?? context.column.getCanSort();

  return (
    <div
      ref={ref}
      className={cn(
        "flex items-center justify-between gap-2",
        {
          "cursor-pointer": context?.column?.getCanSort()
        },
        rootClassName
      )}
      onClick={(event) => {
        event.preventDefault();
        if (context?.column?.getCanSort()) {
          context?.column?.toggleSorting();
        }
      }}
    >
      <div
        className={cn("flex-1 flex items-center", {
          "justify-start": align === "left",
          "justify-center": align === "center",
          "justify-end": align === "right"
        })}
      >
        <span className={cn("inline-block font-bold", labelClassName)}>{label}</span>
      </div>
      {showSorter && (
        <div
          className="flex flex-col -space-y-2"
          title={
            context.column.getCanSort() ?
              context.column.getNextSortingOrder() === "asc" ?
                "Sort ascending"
              : context.column.getNextSortingOrder() === "desc" ?
                "Sort descending"
              : "Clear sort"
            : undefined
          }
        >
          <ChevronUp
            className={cn("stroke-[#BBBBBB]", {
              "stroke-[#41875c]": context?.column?.getIsSorted() === "asc",
              "stroke-[#41875c]/45": context?.column?.getNextSortingOrder() === "asc" && hovering
            })}
            size={16}
            strokeWidth={3}
          />
          <ChevronDown
            className={cn("stroke-[#BBBBBB]", {
              "stroke-[#41875c]": context?.column?.getIsSorted() === "desc",
              "stroke-[#41875c]/45": context?.column?.getNextSortingOrder() === "desc" && hovering
            })}
            size={16}
            strokeWidth={3}
          />
        </div>
      )}
    </div>
  );
};

export default HeaderCell;
